/**
 * Small API + formatting helpers for the "defend your score" re-test
 * feature. Two audiences use these:
 *
 *   - The user-facing `/retest` page (pages/retest.js).
 *   - The admin surface embedded on the player-detail page
 *     (pages/admin_v2/player_detail.js), which lets the admin issue,
 *     extend, review, and read a re-test.
 *
 * Kept tiny — the point is to have a single place that:
 *   1. Names the endpoints so a future rename lands in one file.
 *   2. Formats deadlines in Africa/Lagos (WAT) consistently across both
 *      surfaces. The spec is that every deadline the user or admin
 *      reads is rendered in WAT — matches how metrics_tz.py surfaces
 *      Lagos-local dates on the backend.
 */

import { api } from '../api.js';

// ---- User-facing endpoints ------------------------------------------------

export function getActiveRetest() {
  return api('/retest-quiz');
}

export function startRetest(retestId) {
  return api(`/retest-quiz/${encodeURIComponent(retestId)}/start`, {
    method: 'POST',
  });
}

export function submitRetest(retestId, answers) {
  return api(`/retest-quiz/${encodeURIComponent(retestId)}/submit`, {
    method: 'POST',
    body: { answers },
  });
}

// ---- Admin endpoints ------------------------------------------------------

export function adminSendRetest(body) {
  return api('/admin/retest-quizzes', { method: 'POST', body });
}

export function adminListRetests({ status, expiring_hours, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (expiring_hours) params.set('expiring_hours', String(expiring_hours));
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return api(`/admin/retest-quizzes${qs ? `?${qs}` : ''}`);
}

export function adminGetRetest(retestId) {
  return api(`/admin/retest-quizzes/${encodeURIComponent(retestId)}`);
}

export function adminExtendRetest(retestId, body) {
  return api(`/admin/retest-quizzes/${encodeURIComponent(retestId)}/extend`, {
    method: 'POST',
    body,
  });
}

export function adminReviewRetest(retestId, body) {
  return api(`/admin/retest-quizzes/${encodeURIComponent(retestId)}/review`, {
    method: 'POST',
    body,
  });
}

// ---- WAT formatting -------------------------------------------------------
//
// All deadlines in the retest UI are shown in Africa/Lagos time — the
// player and the admin are both Lagos-based so a UTC ISO string on the
// screen would be a footgun. Lagos is UTC+1 with no DST so
// `Intl.DateTimeFormat({ timeZone: 'Africa/Lagos' })` is stable across
// Node + all browsers we support (Safari 14+, Chrome 90+).

const LAGOS_TZ = 'Africa/Lagos';

/**
 * Human-friendly deadline copy: `Fri 25 Jul, 6:00 PM WAT`. Used in the
 * intro copy ("Complete by ..."), the admin summary, and the deadline
 * badge on active retests.
 */
export function formatLagosDeadline(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LAGOS_TZ,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value || '';
  const weekday = get('weekday');
  const day = get('day').replace(/^0/, ''); // "05" -> "5"
  const month = get('month');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = (get('dayPeriod') || '').toUpperCase();
  return `${weekday} ${day} ${month}, ${hour}:${minute} ${dayPeriod} WAT`;
}

/**
 * Compact date-only version for the "expired on <date>" state.
 */
export function formatLagosDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: LAGOS_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

/**
 * Returns the milliseconds remaining until `iso`. Clamped at 0 so the
 * countdown label never displays a negative value.
 */
export function msRemainingUntil(iso, now = Date.now()) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, t - now);
}

/**
 * `mm:ss` formatter for the countdown. Matches the visual weight of the
 * `Xs` timer used on live_room, but at minute resolution because the
 * re-test window can be up to 30 minutes.
 */
export function formatCountdown(ms) {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ---- Deadline preset math -------------------------------------------------
//
// The admin "Send re-test" modal offers 24h / 48h / 72h presets that
// fill an absolute timestamp. Kept here so the same helper can be used
// by future "Extend deadline" presets.

export function isoAtHoursFromNow(hours) {
  return new Date(Date.now() + hours * 3600 * 1000).toISOString();
}

/**
 * Convert a `<input type="datetime-local">` value (which is naive local
 * time — the browser interprets it as the user's local TZ) to a UTC ISO
 * string the backend accepts. Both admin machines and player devices
 * live in Lagos, so this is equivalent to a UTC+1 → UTC conversion in
 * practice; using the Date constructor's default local-tz interpretation
 * keeps us honest if that ever changes.
 */
export function datetimeLocalToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
